export type SubmitErrorKind =
    | "network"      // no hubo respuesta: sin conexión, DNS, servidor apagado
    | "timeout"      // la petición salió pero se agotó el tiempo
    | "server"       // 5xx: el backend falló
    | "alreadySent"  // 409: el token ya tenía sus respuestas registradas
    | "client";      // resto de 4xx: token inválido, payload mal formado, etc.

export interface ClassifiedSubmitError {
    kind: SubmitErrorKind;
    status?: number;
    retriable: boolean;
    treatAsCompleted: boolean;
}

const TIMEOUT_CODES = ["ECONNABORTED", "ETIMEDOUT"];
const RETRIABLE_CLIENT_STATUS = [408, 425, 429];

function extractStatus(error: unknown): number | undefined {
    const candidate = error as {
        status?: number;
        response?: { status?: number };
    };
    return candidate?.response?.status ?? candidate?.status;
}

function extractCode(error: unknown): string | undefined {
    return (error as { code?: string })?.code;
}

export function classifySubmitError(error: unknown): ClassifiedSubmitError {
    const status = extractStatus(error);
    const code = extractCode(error);

    if (status === undefined) {
        const kind: SubmitErrorKind = code && TIMEOUT_CODES.includes(code) ? "timeout" : "network";
        return {kind, retriable: true, treatAsCompleted: false};
    }

    if (status === 409) {
        return {kind: "alreadySent", status, retriable: false, treatAsCompleted: true};
    }

    if (status >= 500) {
        return {kind: "server", status, retriable: true, treatAsCompleted: false};
    }

    if (RETRIABLE_CLIENT_STATUS.includes(status)) {
        return {kind: "client", status, retriable: true, treatAsCompleted: false};
    }

    return {kind: "client", status, retriable: false, treatAsCompleted: false};
}

export function isRetriableSubmitError(error: unknown): boolean {
    return classifySubmitError(error).retriable;
}
